import { useEffect, useState } from "react";

import { observer } from "mobx-react-lite";
import Link from "next/link";
import { useTranslations } from "next-intl";
import { useWindowSize } from "usehooks-ts";

import * as Profile from "@/components/auth/Profile";
import { useHintHover } from "@/components/hints/useHintHover";
import { Icon } from "@/components/icons/Icon";
import { Sidebar } from "@/components/sidebar";
import { useTooltip } from "@/components/tooltip/Tooltip";
import { useStore } from "@/store";
import { SCREEN_SM } from "@/style/breakpoints";
import { teamPageUrl } from "@/urls";
import { twMerge } from "@/utils/twMerge";
import { useResizeObserver } from "@/utils/useResizeObserver";

import {
  SECTION_HEADER_ACTION_CLASS_NAME,
  SECTION_HEADER_ACTION_ICON_CLASS_NAME,
  SidebarSectionHeader,
} from "../SidebarSectionHeader";

/** Must stay in sync with AVATAR_SIZE below */
const AVATAR_CLASSES = "w-9.5 h-9.5 sm:w-10 sm:h-10";
/** Pixel sizes of AVATAR_CLASSES, used to compute how many avatars fit the row */
const AVATAR_SIZE = { desktop: 38, mobile: 40 };
/** gap-[5px] */
const AVATARS_GAP = 5;

export const TeamMembers = observer(function TeamMembers() {
  const t = useTranslations();
  const { classroom, teams, dialog } = useStore();
  const [isOpen, setIsOpen] = useState(true);
  const team = teams.currentTeam;
  const isPersonal = team?.personal ?? true;
  const { width: windowWidth } = useWindowSize();
  const isMobile = windowWidth < SCREEN_SM;
  const inviteHintHandlers = useHintHover("inviteMembers");
  const { handlerProps: inviteTooltipProps, tooltip: inviteTooltip } = useTooltip({
    title: t("documentsSidebar.invite"),
  });

  const [avatarsRow, setAvatarsRow] = useState<HTMLDivElement | null>(null);
  const [avatarsRowWidth, setAvatarsRowWidth] = useState(0);
  useResizeObserver(avatarsRow, (entry) => setAvatarsRowWidth(entry.contentRect.width));

  useEffect(() => {
    if (!team || isPersonal) return;

    team.membersUX.loadMembers().catch((err) => {
      console.error("Failed to load team members:", err);
    });
  }, [team, isPersonal]);

  if (!team || isPersonal) return null;

  const allMembers = team.membersUX.members;
  if (allMembers.length === 0) return null;

  const avatarSize = isMobile ? AVATAR_SIZE.mobile : AVATAR_SIZE.desktop;
  const visibleMembersCount = Math.floor(
    (avatarsRowWidth + AVATARS_GAP) / (avatarSize + AVATARS_GAP),
  );

  const isRowMeasured = avatarsRowWidth > 0;
  const visibleCount =
    allMembers.length <= visibleMembersCount
      ? allMembers.length
      : Math.max(0, visibleMembersCount - 1);
  const members = isRowMeasured ? allMembers.slice(0, visibleCount) : [];
  const hiddenMembersCount = isRowMeasured ? allMembers.length - members.length : 0;

  return (
    <Sidebar.GroupSection className="mt-1.25">
      <SidebarSectionHeader
        compact
        isOpen={isOpen}
        onToggle={() => setIsOpen(!isOpen)}
        title={t("documentsSidebar.members")}
      >
        {/* Students may see their classmates but not invite anyone
            (docs/plans/student-permissions.md). */}
        {!classroom.isStudentIn(team) && (
          // Tooltip and hint handlers both bind onMouseLeave, so they live on separate elements
          <span className="flex my-auto mr-[1px]" {...inviteTooltipProps}>
            <button
              type="button"
              onClick={() => dialog.teamDialog.open(team, "members")}
              aria-label={t("documentsSidebar.invite")}
              {...inviteHintHandlers}
              className={SECTION_HEADER_ACTION_CLASS_NAME}
            >
              <Icon name="AddUser" className={SECTION_HEADER_ACTION_ICON_CLASS_NAME} />
            </button>
          </span>
        )}
        {inviteTooltip}
      </SidebarSectionHeader>
      {isOpen && (
        <div ref={setAvatarsRow} className="flex items-center gap-[5px] px-[5px] py-[3px]">
          {members.map((member) => (
            <Link
              key={member.user.username}
              href={teamPageUrl({ teamPermalink: member.user.username })}
              className="rounded-full hover:opacity-80 active:opacity-60"
            >
              <Profile.Userpic
                name={member.user.name}
                src={member.user.avatarUrls?.[64]}
                title={member.user.name}
                className={AVATAR_CLASSES}
              />
            </Link>
          ))}
          {hiddenMembersCount > 0 && (
            <button
              type="button"
              onClick={() => dialog.teamDialog.open(team, "members")}
              className={twMerge(
                "flex items-center justify-center shrink-0 rounded-full select-none",
                AVATAR_CLASSES,
                "bg-document-bg shadow-node-outline font-mono text-size-15 text-gray-500",
                "hover:opacity-80 active:opacity-60",
              )}
            >
              +{hiddenMembersCount}
            </button>
          )}
        </div>
      )}
    </Sidebar.GroupSection>
  );
});
